// @flow

import React from 'react';
import gql from 'graphql-tag';
import {FormattedMessage} from 'react-intl';
import {get} from 'lodash';
import PriceSummary, {
    priceSummaryFragments,
    VrboPriceSummaryFragments
} from '@homeaway/react-price-summary';
import {type ListingShape} from '../Hit/Hit';
import './Price.less';
import type {Node} from 'react';

export const fragments = {
    Listing: gql`
        fragment PriceListing on Listing {
            priceSummary: priceSummary {
                # used in calculation of isPricingAccurate on this render implementation
                # it should only be on the PriceSummaryTravelerPriceSummary fragment
                # if that component uses this field. Currently, it does not use.
                priceAccurate
                ...PriceSummaryTravelerPriceSummary
            }
            priceSummarySecondary: priceSummary(
                summary: "displayPriceSecondary"
            ) {
                ...PriceSummaryTravelerPriceSummary
            }
            priceLabel: priceSummary(summary: "priceLabel") {
                priceTypeId
                pricePeriodDescription
            }
            prices {
                ...VrboTravelerPriceSummary
            }
        }
        ${priceSummaryFragments.TravelerPriceSummary}
        ${VrboPriceSummaryFragments.VrboPriceSummary}
    `
};

export const listingHasPrice = (
    listing: ListingShape,
    isPriceAccurateAllowed?: boolean,
    isDatelessSearch?: boolean
): boolean => {
    const {priceSummary} = listing || {};
    const isPriceAccurate = get(priceSummary, 'priceAccurate', false);
    const hasFormattedAmount = get(priceSummary, 'formattedAmount', false);
    const withAcurate =
        !!priceSummary &&
        !!hasFormattedAmount &&
        isPriceAccurate &&
        !isDatelessSearch;
    const withoutAcurate = !!priceSummary && !!hasFormattedAmount;

    return isPriceAccurateAllowed ? withAcurate : withoutAcurate;
};

export type PriceSummaryShape = {
    priceTypeId: string,
    edapEventJson: string,
    formattedAmount: ?string,
    pricePeriodDescription: string
};

export type PriceItemShape = {
    formattedAmount: ?string,
    roundedFormatedAmount: ?string,
    amount: ?number,
    pricePeriodDescription: ?string
};

export type PricesShape = {
    label: ?string,
    perNight: ?PriceItemShape,
    total: ?PriceItemShape
};

export type PriceListingShape = {
    priceSummary: ?PriceSummaryShape,
    priceSummarySecondary: ?PriceSummaryShape,
    priceLabel: ?PriceSummaryShape,
    prices: ?PricesShape
};

export type Props = {
    listing: ListingShape,
    isPriceAccurateAllowed?: boolean,
    isDatelessSearch?: boolean
};

export function Price({
    listing,
    isPriceAccurateAllowed,
    isDatelessSearch
}: Props) {
    if (listingHasPrice(listing, isPriceAccurateAllowed, isDatelessSearch)) {
        return <PriceSummary priceSummary={listing.priceSummary} />;
    }

    return (
        <span className="Price Price--noPrice" data-wdio="Price" data-price="0">
            <FormattedMessage
                id={'ratesNone'}
                description={'no price'}
                defaultMessage={'View details for price'}
            >
                {(txt: Node): Node => (
                    <span className="Price__Fallback">{txt}</span>
                )}
            </FormattedMessage>
        </span>
    );
}
